// WHAT COLOUR A MEETING IS DRAWN IN — one rule, read by all three grids and the list, so
// a meeting can never be one colour in the week view and another in the month view.
//
// THE COLOUR IS THE OWNER'S, not the client's. The calendar's organising question is
// "whose day is this" — the filter switches between the partners, so the colour has to
// answer the same question the filter does. The client already has a colour of its own
// (migration 021) and it is used on the task board and the leads screen; using it here
// too would mean two meetings owned by different partners looking identical.
//
// AN INTERNAL MEETING BELONGS TO BOTH PARTNERS, so it takes NEITHER colour. Painting it
// in the organiser's would claim it as one partner's when it occupies both diaries and
// appears under either filter. It gets a neutral, striped treatment instead — visibly a
// third thing, which is what it is.
//
// The FALLBACK is load-bearing rather than defensive. partners.color is nullable and the
// migration's seed is a no-op on a fresh database (see 056), so on a new install these
// defaults are the only colours there are.
import { MEETING_KIND } from './meetingKind';

// The same two tokens the app's own palette uses (--accent / --green in base.css), in the
// same order migration 056 seeds them, so a seeded database and a fresh one look alike.
const FALLBACK = ['#2563eb', '#059669', '#d97706', '#7c3aed'];

// A partner's colour: their own if set, otherwise a stable one by position in the partner
// list. Position, not id — ids are not guaranteed to start at 1.
export const colorForPartner = (partnerId, partners) => {
  const index = partners.findIndex((p) => String(p.id) === String(partnerId));
  if (index < 0) return 'var(--muted)';
  return partners[index].color || FALLBACK[index % FALLBACK.length];
};

// Everything a chip needs to paint itself. `internal` is returned rather than a colour
// because the two cases are drawn differently, not merely in different hues — the CSS
// gives an internal meeting a striped edge.
export const styleForMeeting = (meeting, partners) => {
  if (meeting.kind === MEETING_KIND.INTERNAL) {
    return { internal: true, color: 'var(--muted)' };
  }
  return { internal: false, color: colorForPartner(meeting.owner_partner_id, partners) };
};

// The inline style a chip carries. Kept here so the grids stay declarative and none of
// them invents its own shade.
export const chipStyle = (meeting, partners) => {
  const { internal, color } = styleForMeeting(meeting, partners);
  return internal
    ? { borderInlineStartColor: 'var(--muted)' }
    : { borderInlineStartColor: color, background: `${color}14` }; // 14 = ~8% alpha
};

export const dotStyle = (meeting, partners) => {
  const { internal, color } = styleForMeeting(meeting, partners);
  return { background: internal ? 'var(--muted)' : color };
};
